import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Input, Table, Button, Modal, Form, message, Space, Tooltip, Popover } from 'antd';
import { EditOutlined, DeleteOutlined, CopyOutlined, PlusOutlined } from '@ant-design/icons';
import { AppConfigManagementPageModel } from '../pageModels/AppConfigManagementPageModel';
import { AppConfigItem } from '../../models/AppConfig';
import { Util } from '../../utils/Util';
import '../styles/AppConfigManagement.css';

const { Search } = Input;

const isJsonString = (str: string): boolean => {
  const trimmed = str.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return false;
  }
  try {
    const parsed = JSON.parse(trimmed);
    return typeof parsed === 'object' && parsed !== null;
  } catch {
    return false;
  }
};

const JsonHighlighted: React.FC<{ value: string }> = ({ value }) => {
  const highlighted = useMemo(() => {
    let formatted: string;
    try {
      formatted = JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      formatted = value;
    }

    const regex = /"(\\.|[^"\\])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g;
    const elements: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let idx = 0;

    while ((match = regex.exec(formatted)) !== null) {
      if (match.index > lastIndex) {
        elements.push(<span key={idx++}>{formatted.slice(lastIndex, match.index)}</span>);
      }
      const token = match[0];
      const isKey = match[2] !== undefined;
      elements.push(
        <span key={idx++} style={{ color: isKey ? '#881391' : '#22863a' }}>
          {token}
        </span>
      );
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < formatted.length) {
      elements.push(<span key={idx++}>{formatted.slice(lastIndex)}</span>);
    }

    return elements;
  }, [value]);

  return (
    <pre style={{
      margin: 0,
      padding: '16px',
      backgroundColor: '#f6f8fa',
      border: '1px solid #d9d9d9',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
      fontFamily: 'Menlo, Monaco, Consolas, monospace',
      fontSize: 13,
      lineHeight: 1.6,
      maxHeight: '60vh',
      overflow: 'auto',
    }}>
      {highlighted}
    </pre>
  );
};

const AppConfigManagement: React.FC = observer(() => {
  const [pageModel] = useState(() => new AppConfigManagementPageModel());
  const [form] = Form.useForm();
  const [jsonViewValue, setJsonViewValue] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    pageModel.loadConfigs();
  }, [pageModel]);

  // 复制到剪贴板
  const copyToClipboard = (text: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    Util.copyToClipboard(text);
  };

  // 显示添加配置弹框
  const showAddModal = () => {
    pageModel.editingConfig = {
      id: '',
      name: '',
      key: '',
      value: '',
      description: ''
    };
    pageModel.isEditModalVisible = true;
    form.resetFields();
  };

  // 显示编辑弹框
  const showEditModal = (config: AppConfigItem) => {
    pageModel.showEditModal(config);
    form.setFieldsValue(config);
  };

  const handleUploadResource = async (file: File) => {
    try {
      const url = await pageModel.uploadConfigResource(file);
      form.setFieldsValue({ value: url });
      pageModel.updateEditingConfig('value', url);
      message.success('上传成功');
    } catch (error) {
      console.error('上传资源失败:', error);
      message.error('上传失败');
    }
  };

  // 保存配置
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      pageModel.updateEditingConfig('name', values.name);
      pageModel.updateEditingConfig('key', values.key);
      pageModel.updateEditingConfig('value', values.value);
      pageModel.updateEditingConfig('description', values.description);
      await pageModel.saveConfig();
      message.success('保存成功');
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error('保存配置失败:', error);
      message.error('保存失败');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '配置名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      onHeaderCell: () => ({ style: { paddingLeft: 0 } }),
      onCell: () => ({ style: { paddingLeft: 0 } }),
    },
    {
      title: '配置Key',
      dataIndex: 'key',
      key: 'key',
      width: 200,
      render: (text: string) => (
        <Space>
          <span>{text}</span>
          <Tooltip title="复制Key">
            <CopyOutlined 
              onClick={(e) => copyToClipboard(text, e)}
              style={{ cursor: 'pointer', color: '#1890ff' }}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: '配置值',
      dataIndex: 'value',
      key: 'value',
      width: 300,
      ellipsis: true,
      render: (text: string) => (
        <Space
          size={4}
          style={{ maxWidth: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          {isJsonString(text) ? (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto', flexShrink: 0 }}
              onClick={() => setJsonViewValue(text)}
            >
              JSON
            </Button>
          ) : (
            <Popover
              title="配置值"
              trigger="click"
              content={(
                <div style={{
                  maxWidth: 480,
                  maxHeight: '50vh',
                  overflow: 'auto',
                  wordBreak: 'break-all',
                  whiteSpace: 'pre-wrap',
                }}>
                  {text}
                </div>
              )}
            >
              <span style={{
                display: 'inline-block',
                maxWidth: 260,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                verticalAlign: 'middle',
                cursor: 'pointer',
              }}>
                {text}
              </span>
            </Popover>
          )}
          <Tooltip title="复制值">
            <CopyOutlined
              onClick={(e) => copyToClipboard(text, e)}
              style={{ cursor: 'pointer', color: '#1890ff', flexShrink: 0 }}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      onHeaderCell: () => ({ style: { paddingRight: 0 } }),
      onCell: () => ({ style: { paddingRight: 0 } }),
      render: (_: any, record: AppConfigItem) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
            style={{ padding: '4px 8px' }}
          >
            编辑
          </Button>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => pageModel.showDeleteModal(record)}
            style={{ padding: '4px 8px' }}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{
      minHeight: 'calc(100vh - 64px)'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16
      }}>
        <Search
          placeholder="搜索配置名称、Key或描述"
          allowClear
          enterButton
          style={{ width: 400 }}
          value={pageModel.searchText}
          onChange={(e) => pageModel.setSearchText(e.target.value)}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={showAddModal}
          style={{ marginLeft: 16 }}
        >
          添加配置
        </Button>
      </div>

      <div>
        <Table
          className="app-config-table"
          columns={columns}
          dataSource={pageModel.filteredConfigs}
          rowKey="id"
          loading={pageModel.loading}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 'max-content' }}
          size="middle"
          style={{
            '--ant-table-border-radius': '0',
            '--ant-table-header-border-radius': '0'
          } as any}
          onRow={(record) => ({
            onClick: () => showEditModal(record),
            style: { cursor: 'pointer' }
          })}
        />
      </div>

      {/* 编辑弹框 */}
      <Modal
        title={pageModel.editingConfig?.id ? '编辑配置' : '添加配置'}
        open={pageModel.isEditModalVisible}
        onOk={handleSave}
        onCancel={() => pageModel.hideEditModal()}
        okText="保存"
        cancelText="取消"
        width={600}
        styles={{
          content: { borderRadius: 0 },
          header: { borderRadius: 0 },
          footer: { borderRadius: 0 }
        }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={pageModel.editingConfig || {}}
        >
          <Form.Item
            name="name"
            label="配置名称"
            rules={[{ required: true, message: '请输入配置名称' }]}
          >
            <Input placeholder="请输入配置名称" style={{ borderRadius: 0 }} />
          </Form.Item>
          <Form.Item
            name="key"
            label="配置Key"
            rules={[{ required: true, message: '请输入配置Key' }]}
          >
            <Input placeholder="请输入配置Key" style={{ borderRadius: 0 }} />
          </Form.Item>
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8
            }}>
              <span>
                <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
                配置值
              </span>
              <Button
                size="small"
                loading={pageModel.uploadingResource}
                onClick={() => fileInputRef.current?.click()}
                style={{ borderRadius: 0 }}
              >
                上传资源
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) {
                  handleUploadResource(file);
                }
              }}
            />
            <Form.Item
              name="value"
              rules={[{ required: true, message: '请输入配置值' }]}
            >
              <Input placeholder="请输入配置值" style={{ borderRadius: 0 }} />
            </Form.Item>
          </div>
          <Form.Item
            name="description"
            label="描述"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <Input.TextArea 
              placeholder="请输入描述" 
              rows={3}
              style={{ borderRadius: 0 }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* JSON 查看弹框 */}
      <Modal
        title="JSON 配置值"
        open={jsonViewValue !== null}
        onCancel={() => setJsonViewValue(null)}
        footer={[
          <Button key="copy" onClick={() => jsonViewValue && copyToClipboard(jsonViewValue)}>
            复制
          </Button>,
          <Button key="close" type="primary" onClick={() => setJsonViewValue(null)}>
            关闭
          </Button>,
        ]}
        width={720}
        styles={{
          content: { borderRadius: 0 },
          header: { borderRadius: 0 },
          footer: { borderRadius: 0 },
        }}
      >
        {jsonViewValue && <JsonHighlighted value={jsonViewValue} />}
      </Modal>

      {/* 删除确认弹框 */}
      <Modal
        title="确认删除"
        open={pageModel.isDeleteModalVisible}
        onOk={() => pageModel.confirmDelete()}
        onCancel={() => pageModel.hideDeleteModal()}
        okText="确认"
        cancelText="取消"
        okType="danger"
        styles={{
          content: { borderRadius: 0 },
          header: { borderRadius: 0 },
          footer: { borderRadius: 0 }
        }}
      >
        <p>确定要删除配置项 "{pageModel.deletingConfig?.name}" 吗？</p>
        <p>删除后无法恢复，请谨慎操作。</p>
      </Modal>
    </div>
  );
});

export default AppConfigManagement; 